/* ============================================================
   FIRE READY STRAPS — THEME JAVASCRIPT
   ============================================================ */

'use strict';

/* ---- Utilities ---- */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function debounce(fn, ms = 250) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

function trapFocus(element) {
  const focusable = element.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  element.addEventListener('keydown', function trap(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
}

/* ---- Cart API ---- */
const CartAPI = {
  async getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  async addItem(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity, properties }] }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add item to cart');
    }
    return res.json();
  },

  async changeItem(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  },

  async removeItem(key) {
    return CartAPI.changeItem(key, 0);
  },
};

/* ---- Cart Drawer ---- */
class CartDrawer {
  constructor() {
    this.drawer = $('#cart-drawer');
    this.body = $('#cart-drawer-body');
    this.countEl = $('#cart-count');
    this.isOpen = false;

    if (!this.drawer) return;

    this.bindEvents();
  }

  bindEvents() {
    $('#cart-toggle')?.addEventListener('click', () => this.open());

    this.drawer.querySelector('.cart-drawer__close')?.addEventListener('click', () => this.close());
    this.drawer.querySelector('.cart-drawer__overlay')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    this.drawer.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        this.removeItem(removeBtn.dataset.removeItem);
      }

      const qtyBtn = e.target.closest('.qty-btn');
      if (qtyBtn) {
        const key = qtyBtn.dataset.key;
        const input = this.drawer.querySelector(`.qty-input[data-key="${key}"]`);
        if (!input) return;
        const current = parseInt(input.value, 10);
        const action = qtyBtn.dataset.action;
        const next = action === 'increase' ? current + 1 : Math.max(0, current - 1);
        input.value = next;
        this.updateItem(key, next);
      }
    });
  }

  open() {
    this.isOpen = true;
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    $('#cart-toggle')?.setAttribute('aria-expanded', 'true');
    this.drawer.querySelector('.cart-drawer__close')?.focus();
    this.refresh();
  }

  close() {
    this.isOpen = false;
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    $('#cart-toggle')?.setAttribute('aria-expanded', 'false');
    $('#cart-toggle')?.focus();
  }

  async refresh() {
    try {
      const cart = await CartAPI.getCart();
      this.updateCount(cart.item_count);
      await this.renderBody();
    } catch (err) {
      console.error('Cart refresh failed:', err);
    }
  }

  async renderBody() {
    if (!this.body) return;
    try {
      const res = await fetch(`${window.location.origin}/cart?view=ajax`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const cart = await CartAPI.getCart();
      this.body.innerHTML = this.buildHTML(cart);
    } catch {
      const cart = await CartAPI.getCart();
      this.body.innerHTML = this.buildHTML(cart);
    }
  }

  buildHTML(cart) {
    if (cart.item_count === 0) {
      return `
        <div class="cart-drawer__empty">
          <p class="cart-drawer__empty-text">Your cart is empty.</p>
          <a href="/collections/all" class="btn btn--primary">Shop All Gear</a>
        </div>`;
    }

    const items = cart.items.map(item => `
      <li class="cart-item" data-key="${item.key}">
        <a href="${item.url}" class="cart-item__media">
          ${item.image ? `<img src="${this.resizeImage(item.image, 100)}" alt="${this.escape(item.title)}" width="80" height="80" loading="lazy" class="cart-item__image">` : ''}
        </a>
        <div class="cart-item__details">
          <a href="${item.url}" class="cart-item__name">${this.escape(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${this.escape(item.variant_title)}</p>` : ''}
          <div class="cart-item__meta">
            <div class="cart-item__quantity qty-selector" data-key="${item.key}">
              <button type="button" class="qty-btn qty-btn--dec" data-action="decrease" data-key="${item.key}" aria-label="Decrease">−</button>
              <input type="number" class="qty-input" value="${item.quantity}" min="0" data-key="${item.key}" aria-label="Quantity">
              <button type="button" class="qty-btn qty-btn--inc" data-action="increase" data-key="${item.key}" aria-label="Increase">+</button>
            </div>
            <span class="cart-item__price">${this.formatMoney(item.final_line_price)}</span>
          </div>
        </div>
        <button type="button" class="cart-item__remove icon-btn" data-remove-item="${item.key}" aria-label="Remove ${this.escape(item.title)}">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </li>`).join('');

    return `
      <ul class="cart-items" id="cart-items" role="list">${items}</ul>
      <div class="cart-drawer__footer">
        <div class="cart-drawer__subtotal">
          <span>Subtotal</span>
          <span id="cart-subtotal">${this.formatMoney(cart.total_price)}</span>
        </div>
        <p class="cart-drawer__note">Taxes and shipping calculated at checkout</p>
        <div class="cart-drawer__actions">
          <a href="/cart" class="btn btn--ghost btn--full">View Cart</a>
          <a href="/checkout" class="btn btn--primary btn--full">Checkout</a>
        </div>
      </div>`;
  }

  updateCount(count) {
    if (!this.countEl) return;
    this.countEl.textContent = count;
    this.countEl.classList.toggle('is-empty', count === 0);
    const toggle = $('#cart-toggle');
    if (toggle) toggle.setAttribute('aria-label', `Open cart (${count})`);
  }

  async removeItem(key) {
    try {
      const cart = await CartAPI.removeItem(key);
      this.updateCount(cart.item_count);
      this.body.innerHTML = this.buildHTML(cart);
    } catch (err) {
      console.error('Remove failed:', err);
    }
  }

  async updateItem(key, quantity) {
    try {
      const cart = await CartAPI.changeItem(key, quantity);
      this.updateCount(cart.item_count);
      const subtotalEl = this.body.querySelector('#cart-subtotal');
      if (subtotalEl) subtotalEl.textContent = this.formatMoney(cart.total_price);
      const itemEl = this.body.querySelector(`[data-key="${key}"]`);
      const item = cart.items.find(i => i.key === key);
      if (itemEl && item) {
        itemEl.querySelector('.cart-item__price').textContent = this.formatMoney(item.final_line_price);
      } else if (itemEl && !item) {
        itemEl.remove();
      }
    } catch (err) {
      console.error('Update failed:', err);
    }
  }

  resizeImage(src, size) {
    return src.replace(/(\.[a-z]+)(\?|$)/, `_${size}x${size}$1$2`);
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

/* ---- Add to Cart ---- */
class AddToCart {
  constructor(cartDrawer) {
    this.cartDrawer = cartDrawer;
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('submit', async (e) => {
      const form = e.target;

      if (document.body.dataset.cartType === 'page') return;
      if (!form.matches('[action*="/cart/add"], [data-product-form]')) return;

      e.preventDefault();
      const btn = form.querySelector('[data-atc-btn]');
      const variantInput = form.querySelector('[name="id"]');
      const qtyInput = form.querySelector('[name="quantity"]');

      if (!variantInput) return;

      const variantId = parseInt(variantInput.value, 10);
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;

      if (btn) {
        btn.disabled = true;
        const label = btn.querySelector('.btn__label');
        const loading = btn.querySelector('.btn__loading');
        if (label) label.hidden = true;
        if (loading) loading.hidden = false;
        else btn.textContent = 'Adding…';
      }

      try {
        await CartAPI.addItem(variantId, quantity);
        if (this.cartDrawer) {
          this.cartDrawer.refresh();
          this.cartDrawer.open();
        }
      } catch (err) {
        this.showError(form, err.message);
      } finally {
        if (btn) {
          btn.disabled = false;
          const label = btn.querySelector('.btn__label');
          const loading = btn.querySelector('.btn__loading');
          if (label) label.hidden = false;
          if (loading) loading.hidden = true;
          else btn.textContent = 'Add to Cart';
        }
      }
    });
  }

  showError(form, message) {
    let errEl = form.querySelector('.atc-error');
    if (!errEl) {
      errEl = document.createElement('p');
      errEl.className = 'atc-error';
      errEl.style.cssText = 'color: var(--color-danger); font-size: 0.875rem; margin-top: 0.5rem;';
      form.appendChild(errEl);
    }
    errEl.textContent = message;
    setTimeout(() => errEl.remove(), 4000);
  }
}

/* ---- Search Drawer ---- */
class SearchDrawer {
  constructor() {
    this.drawer = $('#search-drawer');
    this.input = this.drawer?.querySelector('.search-drawer__input');
    this.isOpen = false;

    if (!this.drawer) return;
    this.bindEvents();
  }

  bindEvents() {
    $('#search-toggle')?.addEventListener('click', () => this.toggle());

    this.drawer.querySelector('.search-drawer__overlay')?.addEventListener('click', () => this.close());
    this.drawer.querySelector('.search-drawer__close')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    $('#search-toggle')?.setAttribute('aria-expanded', 'true');
    setTimeout(() => this.input?.focus(), 50);
  }

  close() {
    this.isOpen = false;
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    $('#search-toggle')?.setAttribute('aria-expanded', 'false');
  }
}

/* ---- Mobile Menu ---- */
class MobileMenu {
  constructor() {
    this.menu = $('#mobile-menu');
    this.isOpen = false;

    if (!this.menu) return;
    this.bindEvents();
  }

  bindEvents() {
    $('#mobile-menu-toggle')?.addEventListener('click', () => this.open());

    this.menu.querySelector('.mobile-menu__overlay')?.addEventListener('click', () => this.close());
    this.menu.querySelector('.mobile-menu__close')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    $$('.mobile-menu__toggle', this.menu).forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.mobile-menu__item');
        item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', item.classList.contains('is-open'));
      });
    });
  }

  open() {
    this.isOpen = true;
    this.menu.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    $('#mobile-menu-toggle')?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.isOpen = false;
    this.menu.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    $('#mobile-menu-toggle')?.setAttribute('aria-expanded', 'false');
    $('#mobile-menu-toggle')?.focus();
  }
}

/* ---- Announcement Bar ---- */
class AnnouncementBar {
  constructor() {
    this.bar = $('#announcement-bar');
    this.slides = $$('.announcement-bar__slide');
    this.current = 0;

    if (!this.bar) return;

    $('#announcement-dismiss')?.addEventListener('click', () => this.dismiss());

    if (sessionStorage.getItem('announcement-dismissed') === 'true') {
      this.bar.style.display = 'none';
    }

    if (this.slides.length > 1) {
      const speed = parseInt(this.bar.dataset.rotationSpeed || '4', 10) * 1000;
      setInterval(() => this.next(), speed);
    }
  }

  next() {
    this.slides[this.current].classList.remove('is-active');
    this.current = (this.current + 1) % this.slides.length;
    this.slides[this.current].classList.add('is-active');
  }

  dismiss() {
    this.bar.style.height = this.bar.offsetHeight + 'px';
    this.bar.offsetHeight;
    this.bar.style.transition = 'height 300ms ease, opacity 300ms ease';
    this.bar.style.height = '0';
    this.bar.style.opacity = '0';
    this.bar.style.overflow = 'hidden';
    setTimeout(() => { this.bar.style.display = 'none'; }, 300);
    sessionStorage.setItem('announcement-dismissed', 'true');
  }
}

/* ---- Sticky Header ---- */
class StickyHeader {
  constructor() {
    this.header = $('#site-header');
    if (!this.header) return;

    let lastScroll = 0;
    window.addEventListener('scroll', debounce(() => {
      const scroll = window.scrollY;
      this.header.classList.toggle('is-scrolled', scroll > 20);
      lastScroll = scroll;
    }, 50), { passive: true });
  }
}

/* ---- Product Gallery ---- */
class ProductGallery {
  constructor() {
    this.gallery = $('#product-gallery');
    if (!this.gallery) return;

    this.slides = $$('.product-gallery__slide', this.gallery);
    this.thumbs = $$('.product-gallery__thumb', this.gallery);

    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.goTo(i));
    });
  }

  goTo(index) {
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
  }
}

/* ---- Product Variants ---- */
class ProductVariants {
  constructor() {
    const form = $('[data-product-form]');
    if (!form) return;

    const dataEl = $('[id^="product-json-"]');
    if (!dataEl) return;

    this.product = JSON.parse(dataEl.textContent);
    this.form = form;
    this.variantInput = form.querySelector('[name="id"]');
    this.priceEl = $('#product-price');
    this.atcBtn = $('#add-to-cart-btn');

    this.bindEvents();
  }

  bindEvents() {
    $$('.variant-selector__input', this.form).forEach(input => {
      input.addEventListener('change', () => this.onOptionChange());
    });
  }

  onOptionChange() {
    const selected = [];
    for (let i = 0; i < this.product.options.length; i++) {
      const checked = this.form.querySelector(`.variant-selector__input[data-option-index="${i}"]:checked`);
      if (checked) {
        selected.push(checked.value);
        const valueEl = $(`#option-value-${i + 1}`);
        if (valueEl) valueEl.textContent = checked.value;
      }
    }

    const variant = this.product.variants.find(v =>
      v.options.every((opt, i) => opt === selected[i])
    );

    if (!variant) return;

    this.variantInput.value = variant.id;

    if (this.priceEl) {
      const price = this.formatMoney(variant.price);
      const compare = variant.compare_at_price > variant.price
        ? `<s class="price price--compare">${this.formatMoney(variant.compare_at_price)}</s>`
        : '';
      const saleClass = variant.compare_at_price > variant.price ? 'price--sale' : '';
      this.priceEl.innerHTML = `
        <span class="price ${saleClass}">${price}</span>
        ${compare}`;
    }

    if (this.atcBtn) {
      if (variant.available) {
        this.atcBtn.disabled = false;
        this.atcBtn.querySelector('.btn__label').textContent = 'Add to Cart';
        this.atcBtn.classList.remove('btn--disabled');
      } else {
        this.atcBtn.disabled = true;
        this.atcBtn.querySelector('.btn__label').textContent = 'Sold Out';
        this.atcBtn.classList.add('btn--disabled');
      }
    }

    window.history.replaceState({}, '', `?variant=${variant.id}`);
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

/* ---- Product Quantity (product page) ---- */
class ProductQuantity {
  constructor() {
    const dec = $('[data-product-qty-dec]');
    const inc = $('[data-product-qty-inc]');
    const input = $('[data-product-qty]');

    if (!dec || !inc || !input) return;

    dec.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      if (val > 1) input.value = val - 1;
    });

    inc.addEventListener('click', () => {
      input.value = parseInt(input.value, 10) + 1;
    });
  }
}

/* ---- Cart Page (qty updates on cart page) ---- */
class CartPage {
  constructor(cartDrawer) {
    this.page = $('.main-cart');
    this.cartDrawer = cartDrawer;
    if (!this.page) return;
    this.bindEvents();
  }

  bindEvents() {
    this.page.addEventListener('click', async (e) => {
      const qtyBtn = e.target.closest('.qty-btn');
      if (qtyBtn) {
        const key = qtyBtn.dataset.key;
        const input = this.page.querySelector(`.qty-input[data-key="${key}"]`);
        if (!input) return;
        const current = parseInt(input.value, 10);
        const action = qtyBtn.dataset.action;
        const next = action === 'increase' ? current + 1 : Math.max(0, current - 1);
        input.value = next;
        await this.update(key, next);
      }

      const removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        await this.update(removeBtn.dataset.removeItem, 0);
      }
    });
  }

  async update(key, quantity) {
    try {
      const cart = await CartAPI.changeItem(key, quantity);
      if (this.cartDrawer) this.cartDrawer.updateCount(cart.item_count);

      if (quantity === 0) {
        this.page.querySelector(`[data-key="${key}"]`)?.remove();
      }

      const subtotalEl = $('#cart-page-subtotal');
      if (subtotalEl) {
        subtotalEl.textContent = '$' + (cart.total_price / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }

      if (cart.item_count === 0) {
        window.location.reload();
      }
    } catch (err) {
      console.error('Cart update failed:', err);
    }
  }
}

/* ---- Collection Sort ---- */
class CollectionSort {
  constructor() {
    const sortSelect = $('#collection-sort');
    if (!sortSelect) return;

    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }
}

/* ---- Collection Filters ---- */
class CollectionFilters {
  constructor() {
    const toggle = $('#filter-toggle');
    const panel = $('#collection-filters');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      panel.setAttribute('aria-hidden', String(open));
      panel.style.display = open ? 'none' : 'block';
    });
  }
}

/* ---- Scroll Animations ---- */
class ScrollAnimations {
  constructor() {
    if (!document.body.classList.contains('animations-enabled')) return;

    const items = $$('[data-animate-item]');
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    items.forEach(item => observer.observe(item));
  }
}

/* ---- Marquee Text (dynamic duration) ---- */
class Marquee {
  constructor() {
    const marquees = $$('.marquee-text__track');
    if (!marquees.length) return;

    // Speed in pixels per second for each preset
    const speedMap = {
      '40s': 60,   // Slow
      '25s': 100,  // Medium
      '12s': 200   // Fast
    };

    marquees.forEach(track => {
      const settingsDuration = track.style.getPropertyValue('--marquee-duration')?.trim();
      const pxPerSec = speedMap[settingsDuration] || 100;

      // Wait for layout to settle, then measure
      requestAnimationFrame(() => {
        const trackWidth = track.scrollWidth;
        if (trackWidth > 0) {
          const calculatedDuration = trackWidth / pxPerSec;
          track.style.setProperty('--marquee-duration', `${calculatedDuration}s`);
        }
      });
    });
  }
}

/* ---- Init ---- */
document.addEventListener('DOMContentLoaded', () => {
  const cartDrawer = new CartDrawer();
  new AddToCart(cartDrawer);
  new SearchDrawer();
  new MobileMenu();
  new AnnouncementBar();
  new StickyHeader();
  new ProductGallery();
  new ProductVariants();
  new ProductQuantity();
  new CartPage(cartDrawer);
  new CollectionSort();
  new CollectionFilters();
  new ScrollAnimations();
  new Marquee();
});
